<template>
    <div
        class="flex w-full shrink-0 lg:max-h-[26.25rem] lg:max-w-[31.25rem] xl:max-h-[37.5rem] xl:max-w-[42.5rem]">
        <div
            class="mr-3 flex max-h-[592px] w-[4.5rem] shrink-0 flex-col space-y-3 max-lg:hidden">
            <ShotVideo
                v-if="videoLink"
                :link="videoLink"
                :preview="videoPreview" />
            <ArrowButton
                v-if="images.length !== 1"
                rotation="top"
                arrow-class="size-3"
                sizes-class="w-full rounded-12 h-7 shrink-0"
                :disabled="activeThumbIndex === 0"
                @click="mainSlider?.slidePrev()" />
            <div
                ref="thumbs-slider"
                class="swiper w-full">
                <div class="swiper-wrapper h-full flex-col">
                    <div
                        v-for="(image, index) in images"
                        :key="image.alt || index"
                        :class="{
                            'swiper-slide-thumb-active': index === 1,
                        }"
                        class="swiper-slide group !h-auto overflow-hidden">
                        <picture>
                            <source
                                v-if="image.webp_url"
                                type="image/webp"
                                :srcset="
                                    getMediaLink(image.webp_url, 200) || ''
                                " />

                            <source
                                v-else-if="!image.url && !image.webp_url"
                                type="image/webp"
                                srcset="
                                    /assets/images/ui/placeholder/webp/cromi.webp
                                " />

                            <img
                                loading="lazy"
                                :width="image.width || 416"
                                :height="image.height || 234"
                                :alt="image.alt || image.url"
                                :src="
                                    getMediaLink(image.url, 200) ||
                                    '/assets/images/ui/placeholder/jpg-png/cromi.png'
                                "
                                class="size-[4.5rem] rounded-12 border-accent object-cover transition-all group-[.swiper-slide-thumb-active]:rounded-8 group-[.swiper-slide-thumb-active]:border-4" />
                        </picture>
                    </div>
                </div>
            </div>
            <ArrowButton
                v-if="images.length !== 1"
                rotation="bottom"
                arrow-class="size-3"
                sizes-class="w-full rounded-12 h-7 shrink-0"
                :disabled="activeThumbIndex === images.length - 1"
                @click="mainSlider?.slideNext()" />
        </div>
        <div
            ref="main-slider"
            class="swiper overflow-hidden rounded-12 max-lg:rounded-8">
            <div class="swiper-wrapper flex">
                <div
                    v-for="(image, index) in images"
                    :key="image.alt || index"
                    class="swiper-slide rounded-12 max-lg:!size-[17.5rem] max-lg:rounded-8">
                    <picture>
                        <source
                            v-if="image.webp_url"
                            type="image/webp"
                            :srcset="getMediaLink(image.webp_url) || ''" />

                        <source
                            v-else-if="!image.url && !image.webp_url"
                            type="image/webp"
                            srcset="
                                /assets/images/ui/placeholder/webp/cromi.webp
                            " />

                        <img
                            loading="lazy"
                            :width="image.width || 416"
                            :height="image.height || 234"
                            :alt="image.alt || image.url"
                            :src="
                                getMediaLink(image.url) ||
                                '/assets/images/ui/placeholder/jpg-png/cromi.png'
                            "
                            class="aspect-square w-full rounded-12 object-cover max-lg:w-[17.5rem] max-lg:rounded-8" />
                    </picture>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { Swiper } from 'swiper'
import 'swiper/css'
import 'swiper/css/free-mode'
import 'swiper/css/navigation'
import 'swiper/css/thumbs'
import { Navigation, Thumbs } from 'swiper/modules'
import type { Image } from '~/types/global.types'

import ArrowButton from '~/components/base/arrow-button/ArrowButton.vue'
import ShotVideo from '~/components/base/shot-video/ShotVideo.vue'

defineProps({
    images: {
        type: Array as PropType<Image[]>,
        default: () => [],
    },
    videoLink: {
        type: [String, null] as PropType<string | null>,
        default: null,
    },
    videoPreview: {
        type: [Object, null] as PropType<Image | null>,
        default: null,
    },
})

const mainSliderRef = useTemplateRef('main-slider')
const mainSlider = ref<Swiper | null>(null)

const thumbsSliderRef = useTemplateRef('thumbs-slider')
const activeThumbIndex = ref(0)

let thumbsSlider: Swiper | null = null

onMounted(() => {
    if (!mainSliderRef.value || !thumbsSliderRef.value) return

    thumbsSlider = new Swiper(thumbsSliderRef.value, {
        modules: [Navigation],
        watchSlidesProgress: true,
        slidesPerView: 'auto',
        direction: 'vertical',
        freeMode: true,
        spaceBetween: 8,
    })

    mainSlider.value = new Swiper(mainSliderRef.value, {
        modules: [Thumbs],
        thumbs: {
            swiper: thumbsSlider,
            autoScrollOffset: 0.5,
        },
        slidesPerView: 'auto',
        spaceBetween: 20,
        breakpoints: {
            1024: {
                slidesPerView: 1,
                spaceBetween: 0,
            },
        },
        on: {
            activeIndexChange: (swiper) => {
                activeThumbIndex.value = swiper.activeIndex
            },
        },
    })
})

onUnmounted(() => {
    if (mainSlider.value) mainSlider.value.destroy()
    if (thumbsSlider) thumbsSlider.destroy()
})
</script>
